import React from "react";
import PageShell from "@/components/PageShell";

const ZONES = [
    {
        region: "United Kingdom",
        standard: "Free over £150 / £6 under",
        express: "£12 — Next day",
        time: "1–2 business days",
    },
    {
        region: "European Union",
        standard: "Free over €175 / €12 under",
        express: "€22 — 24–48h",
        time: "2–4 business days",
    },
    {
        region: "United States / Canada",
        standard: "Free over $200 / $15 under",
        express: "$35 — DHL Express",
        time: "3–6 business days",
    },
    {
        region: "Rest of World",
        standard: "Calculated at checkout",
        express: "DHL Express available",
        time: "5–10 business days",
    },
];

const Shipping = () => {
    return (
        <PageShell
            kicker="[Logistics / Service]"
            title="Shipping &"
            accent="Returns."
            intro="Tracked, insured, and dispatched within 2 business days of every order. Returns honoured for 30 days from delivery."
            testid="shipping-page"
        >
            <section className="px-4 md:px-8 py-10 border-b-4 border-black">
                <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    // 01 / Rates & Zones
                </span>
                <h2 className="font-heading text-4xl md:text-5xl uppercase leading-none mt-1">
                    Shipping Zones
                </h2>
                <div className="mt-6 overflow-x-auto border-2 border-black">
                    <table className="w-full font-mono-ui text-xs">
                        <thead className="bg-black text-white uppercase tracking-[0.2em]">
                            <tr>
                                <Th>Region</Th>
                                <Th>Standard</Th>
                                <Th>Express</Th>
                                <Th>Delivery</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {ZONES.map((z, i) => (
                                <tr
                                    key={z.region}
                                    className={i % 2 === 0 ? "bg-[#f3f3f3] text-black" : "bg-white text-black"}
                                    data-testid={`shipping-row-${i}`}
                                >
                                    <Td><strong>{z.region}</strong></Td>
                                    <Td>{z.standard}</Td>
                                    <Td>{z.express}</Td>
                                    <Td>{z.time}</Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 border-b-4 border-black">
                <div className="p-6 md:p-12 md:border-r-2 border-black">
                    <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                        // 02 / Returns
                    </span>
                    <h2 className="font-heading text-4xl md:text-5xl uppercase leading-none mt-1">
                        30-Day Return Window
                    </h2>
                    <ul className="mt-6 space-y-3 text-sm md:text-base list-disc list-outside ml-5">
                        <li>Unworn, unwashed, with original tags attached.</li>
                        <li>Footwear must be tried on indoors only and returned in the original box.</li>
                        <li>Sale and final-sale items are non-returnable.</li>
                        <li>Refunds processed within 5 business days of receipt.</li>
                    </ul>
                </div>
                <div className="bg-black p-6 text-white md:p-12">
                    <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-white/50">
                        // 03 / How To Return
                    </span>
                    <h2 className="font-heading text-4xl md:text-5xl uppercase leading-none mt-1">
                        Three Steps.
                    </h2>
                    <ol className="mt-6 space-y-4 text-sm md:text-base">
                        <Step n="01" text="Email hello@uknitwear.com with your order number and the reason." />
                        <Step n="02" text="Receive a prepaid return label (UK/EU) or returns instructions (RoW)." />
                        <Step n="03" text="Drop the package off. Refund hits your card within 5 business days." />
                    </ol>
                </div>
            </section>

            <section className="grid grid-cols-1 gap-6 border-b-4 border-black px-4 py-10 md:grid-cols-3 md:px-8">
                <Info title="Duties & Taxes" body="DDP (delivered-duty-paid) for UK, EU, US, CA, JP, AU. All other regions may incur local charges." />
                <Info title="Pre-Orders" body="Pre-orders ship on the date listed on the product page. Mixed orders are held until all items are ready." />
                <Info title="Lost In Transit" body="Anything marked delivered but missing — email us within 7 days and we'll lodge a claim with the courier." />
            </section>
        </PageShell>
    );
};

const Th = ({ children }) => (
    <th className="text-left p-3 font-mono-ui text-[10px]">{children}</th>
);
const Td = ({ children }) => <td className="border-t border-dotted border-black/45 p-3">{children}</td>;
const Step = ({ n, text }) => (
    <li className="flex gap-4">
        <span className="font-heading text-3xl text-hazard leading-none">{n}</span>
        <span className="text-white/85">{text}</span>
    </li>
);
const Info = ({ title, body }) => (
    <div className="border-2 border-black bg-white p-5 text-black">
        <h4 className="font-heading text-2xl uppercase">{title}</h4>
        <p className="mt-2 text-sm">{body}</p>
    </div>
);

export default Shipping;
